import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ filters, onFiltersChange, onClearFilters, isOpen, onToggle }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (category, key, value) => {
    const newFilters = {
      ...localFilters,
      [category]: {
        ...localFilters?.[category],
        [key]: value
      }
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePriceChange = (type, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    handleFilterChange('price', type, numValue);
  };

  const handleCheckboxChange = (category, key, checked) => {
    const currentArray = localFilters?.[category]?.[key] || [];
    const newArray = checked 
      ? [...currentArray, key]
      : currentArray?.filter(item => item !== key);
    
    handleFilterChange(category, key, newArray);
  };

  const FilterSection = ({ title, icon, children, defaultOpen = true }) => {
    const [isExpanded, setIsExpanded] = useState(defaultOpen);
    
    return (
      <div className="border-b border-border last:border-b-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Icon name={icon} size={16} className="text-primary" />
            <span className="font-medium text-text-primary">{title}</span>
          </div>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={16} 
            className="text-muted-foreground" 
          />
        </button>
        {isExpanded && (
          <div className="px-4 pb-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  const brands = [
    'Dyson', 'Shark', 'Bissell', 'Hoover', 'Eureka', 'Black+Decker', 'Tineco', 'Miele'
  ];

  const types = [
    'Upright', 'Canister', 'Stick', 'Handheld', 'Robot', 'Wet/Dry'
  ];

  const features = [
    { key: 'bagless', label: 'Bagless Design' },
    { key: 'hepaFilter', label: 'HEPA Filter' },
    { key: 'petHair', label: 'Pet Hair Specialist' },
    { key: 'lightweight', label: 'Lightweight (< 10 lbs)' },
    { key: 'cordless', label: 'Cordless' },
    { key: 'selfCleaning', label: 'Self-Cleaning Brush' }
  ];

  return (
    <div className={`bg-white border border-border rounded-lg transition-all duration-300 ${
      isOpen ? 'w-80' : 'w-12'
    } flex-shrink-0`}>
      {/* Toggle Button */}
      <div className="p-3 border-b border-border">
        <Button
          variant="ghost"
          size="sm"
          iconName={isOpen ? "X" : "Filter"}
          onClick={onToggle}
          className="w-full justify-center"
        >
          {isOpen && "Close Filters"}
        </Button>
      </div>
      {isOpen && (
        <div className="h-full overflow-y-auto">
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Filters</h3>
              <Button
                variant="ghost"
                size="xs"
                onClick={onClearFilters}
                className="text-muted-foreground hover:text-destructive"
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Search */}
          <FilterSection title="Search" icon="Search">
            <Input
              type="search"
              placeholder="Search by model name..."
              value={localFilters?.search || ''}
              onChange={(e) => handleFilterChange('general', 'search', e?.target?.value)}
              className="w-full"
            />
          </FilterSection>

          {/* Price Range */}
          <FilterSection title="Price Range" icon="DollarSign">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={localFilters?.price?.min || ''}
                  onChange={(e) => handlePriceChange('min', e?.target?.value)}
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={localFilters?.price?.max || ''}
                  onChange={(e) => handlePriceChange('max', e?.target?.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Under $100', min: 0, max: 100 },
                  { label: '$100-$300', min: 100, max: 300 },
                  { label: '$300-$500', min: 300, max: 500 },
                  { label: 'Over $500', min: 500, max: null }
                ]?.map((range) => (
                  <Button
                    key={range?.label}
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      handlePriceChange('min', range?.min);
                      handlePriceChange('max', range?.max);
                    }}
                    className="text-xs"
                  >
                    {range?.label}
                  </Button>
                ))}
              </div>
            </div>
          </FilterSection>

          {/* Brand */}
          <FilterSection title="Brand" icon="Tag">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {brands?.map((brand) => (
                <Checkbox
                  key={brand}
                  label={brand}
                  checked={localFilters?.brands?.includes(brand) || false}
                  onChange={(e) => {
                    const currentBrands = localFilters?.brands || [];
                    const newBrands = e?.target?.checked
                      ? [...currentBrands, brand]
                      : currentBrands?.filter(b => b !== brand);
                    handleFilterChange('general', 'brands', newBrands);
                  }}
                />
              ))}
            </div>
          </FilterSection>

          {/* Type */}
          <FilterSection title="Type" icon="Package">
            <div className="space-y-2">
              {types?.map((type) => (
                <Checkbox
                  key={type}
                  label={type}
                  checked={localFilters?.types?.includes(type) || false}
                  onChange={(e) => {
                    const currentTypes = localFilters?.types || [];
                    const newTypes = e?.target?.checked
                      ? [...currentTypes, type]
                      : currentTypes?.filter(t => t !== type);
                    handleFilterChange('general', 'types', newTypes);
                  }}
                />
              ))}
            </div>
          </FilterSection>

          {/* Rating */}
          <FilterSection title="Minimum Rating" icon="Star">
            <div className="space-y-2">
              {[4.5, 4.0, 3.5, 3.0]?.map((rating) => (
                <Checkbox
                  key={rating}
                  label={
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-yellow-500" />
                      <span>{rating}+ Stars</span>
                    </div>
                  }
                  checked={localFilters?.rating === rating}
                  onChange={(e) => {
                    handleFilterChange('general', 'rating', e?.target?.checked ? rating : null);
                  }}
                />
              ))}
            </div>
          </FilterSection>

          {/* Features */}
          <FilterSection title="Features" icon="Settings">
            <div className="space-y-2">
              {features?.map((feature) => (
                <Checkbox
                  key={feature?.key}
                  label={feature?.label}
                  checked={localFilters?.features?.[feature?.key] || false}
                  onChange={(e) => {
                    handleFilterChange('features', feature?.key, e?.target?.checked);
                  }}
                />
              ))}
            </div>
          </FilterSection>

          {/* Performance */}
          <FilterSection title="Performance Score" icon="BarChart3">
            <div className="space-y-3">
              <Input
                type="range"
                min="0"
                max="100"
                value={localFilters?.performance?.min || 0}
                onChange={(e) => handleFilterChange('performance', 'min', parseInt(e?.target?.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0</span>
                <span className="font-medium text-text-primary">
                  {localFilters?.performance?.min || 0}+ Score
                </span>
                <span>100</span>
              </div>
            </div>
          </FilterSection>

          {/* Weight */}
          <FilterSection title="Weight" icon="Weight">
            <div className="space-y-2">
              {[
                { label: 'Ultra Light (< 5 lbs)', max: 5 },
                { label: 'Light (5-10 lbs)', min: 5, max: 10 },
                { label: 'Medium (10-15 lbs)', min: 10, max: 15 },
                { label: 'Heavy (15+ lbs)', min: 15 }
              ]?.map((range, index) => (
                <Checkbox
                  key={index}
                  label={range?.label}
                  checked={
                    localFilters?.weight?.min === range?.min && 
                    localFilters?.weight?.max === range?.max
                  }
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      handleFilterChange('weight', 'min', range?.min || 0);
                      handleFilterChange('weight', 'max', range?.max || 999);
                    } else {
                      handleFilterChange('weight', 'min', null);
                      handleFilterChange('weight', 'max', null);
                    }
                  }}
                />
              ))}
            </div>
          </FilterSection>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;